import { createFileRoute } from '@tanstack/react-router';
import { useLocale, useTranslations } from 'use-intl';
import {
  Header,
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  MainScrollArea,
  MainHeader,
  Main,
  MainContainer,
  MainHeaderBackButton,
} from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { HugeiconsIcon } from '@hugeicons/react';
import { Building06Icon, Clock04Icon } from '@hugeicons/core-free-icons';
import { UserAvatar } from '@/app/ui/components/user-avatar';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/app/ui/components/tooltip';
import { format } from 'date-fns';
import { dateFormats } from '@/app/utils/constants';
import { getLocalizedDateDistance } from '@/app/utils/dates';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';

export const Route = createFileRoute('/_authed/project/$projectId/_layout/(home)/activity')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const locale = useLocale();
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  return (
    <Main>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/">
                <HugeiconsIcon icon={Building06Icon} />
                {t('pages.homepage.breadcrumbs.projects')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={Clock04Icon} />
                {t('pages.activityLog.title')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton />
            {t('pages.activityLog.title')}
          </H1>
        </MainHeader>
        <MainContainer>
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <UserAvatar
                size={24}
                user={{
                  id: project.projectOwner.id,
                  email: project.projectOwner.email,
                  name: `${project.projectOwner.firstName} ${project.projectOwner.lastName}`,
                }}
              />
              <div className="mt-0.5 flex-wrap">
                <span className="font-medium">
                  {project.projectOwner.firstName} {project.projectOwner.lastName}
                </span>{' '}
                <span className="text-primary-11">
                  {t('pages.activityLog.activities.projectCreated')}
                </span>{' '}
                <Tooltip>
                  <TooltipTrigger className="text-muted lowercase font-medium">
                    {getLocalizedDateDistance(project.createdAt, locale)}
                  </TooltipTrigger>
                  <TooltipPopup>{format(project.createdAt, dateFormats.withTime)}</TooltipPopup>
                </Tooltip>
              </div>
            </div>
          </div>
        </MainContainer>
      </MainScrollArea>
    </Main>
  );
}
